/**
 * 💬 Intent Buttons Component
 * Multi-level interest indication system
 * Böngészés → Érdekel → Komolyan érdekel → Beszélnék → Ajánlatot tennék
 */

import React, { useState } from 'react';
import { recordIntent, INTENT_LEVELS } from '../../services/secretDealsService';

const IntentButtons = ({ 
  propertyId, 
  buyerId, 
  currentIntent = null,
  onIntentChange,
  compact = false 
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedIntent, setSelectedIntent] = useState(currentIntent);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleIntentClick = async (intentKey) => {
    if (loading) return;
    
    // For high-intent actions, show confirmation
    if (intentKey === 'MAKE_OFFER' || intentKey === 'WANT_TO_TALK') {
      setSelectedIntent(intentKey);
      setShowConfirmation(true);
      return;
    }

    await submitIntent(intentKey);
  };

  const submitIntent = async (intentKey) => {
    setLoading(true);
    try {
      const result = await recordIntent(buyerId, propertyId, intentKey);
      setSelectedIntent(intentKey);
      setShowConfirmation(false);
      if (onIntentChange) onIntentChange(intentKey, result);
    } catch (error) {
      console.error('Error recording intent:', error);
    } finally {
      setLoading(false);
    }
  };

  const getButtonStyle = (intentKey) => {
    const isSelected = selectedIntent === intentKey;
    
    const styles = {
      BROWSING: {
        base: 'bg-gray-700 hover:bg-gray-600',
        selected: 'bg-gray-500 ring-2 ring-gray-400'
      },
      INTERESTED: {
        base: 'bg-blue-600/30 hover:bg-blue-600/50 border border-blue-500/50',
        selected: 'bg-blue-600 ring-2 ring-blue-400'
      },
      SERIOUSLY_INTERESTED: {
        base: 'bg-orange-600/30 hover:bg-orange-600/50 border border-orange-500/50',
        selected: 'bg-orange-600 ring-2 ring-orange-400'
      },
      WANT_TO_TALK: {
        base: 'bg-purple-600/30 hover:bg-purple-600/50 border border-purple-500/50',
        selected: 'bg-purple-600 ring-2 ring-purple-400'
      },
      MAKE_OFFER: {
        base: 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500',
        selected: 'ring-2 ring-green-400 shadow-lg shadow-green-500/30'
      }
    };

    const state = isSelected ? 'selected' : 'base';
    return styles[intentKey]?.[state] || styles.BROWSING.base;
  };

  const intentButtons = [
    { key: 'BROWSING', ...INTENT_LEVELS.BROWSING },
    { key: 'INTERESTED', ...INTENT_LEVELS.INTERESTED },
    { key: 'SERIOUSLY_INTERESTED', ...INTENT_LEVELS.SERIOUSLY_INTERESTED },
    { key: 'WANT_TO_TALK', ...INTENT_LEVELS.WANT_TO_TALK },
    { key: 'MAKE_OFFER', ...INTENT_LEVELS.MAKE_OFFER }
  ];

  if (compact) {
    // Compact horizontal layout
    return (
      <div className="flex items-center gap-2">
        {intentButtons.slice(0, 3).map((intent) => (
          <button
            key={intent.key}
            onClick={() => handleIntentClick(intent.key)}
            disabled={loading}
            className={`
              px-3 py-1.5 rounded-full text-xs font-semibold
              transition-all duration-200
              ${getButtonStyle(intent.key)}
              ${selectedIntent === intent.key ? 'text-white' : 'text-gray-300'}
              disabled:opacity-50
            `}
          >
            {intent.icon} {intent.label}
          </button>
        ))}
        
        {/* High intent dropdown trigger */}
        <div className="relative group">
          <button className="px-2 py-1.5 rounded-full text-xs bg-gray-700 hover:bg-gray-600 text-white">
            ▼
          </button>
          <div className="absolute right-0 top-full mt-1 hidden group-hover:block z-50">
            <div className="bg-black/95 backdrop-blur-xl border border-white/10 rounded-xl p-2 shadow-2xl min-w-[140px]">
              {intentButtons.slice(3).map((intent) => (
                <button
                  key={intent.key}
                  onClick={() => handleIntentClick(intent.key)}
                  disabled={loading}
                  className={`
                    w-full px-3 py-2 rounded-lg text-sm text-left
                    transition-all duration-200 mb-1 last:mb-0
                    ${getButtonStyle(intent.key)}
                    ${selectedIntent === intent.key ? 'text-white' : 'text-gray-300'}
                  `}
                >
                  {intent.icon} {intent.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Full vertical layout
  return (
    <div className="space-y-2">
      <div className="text-xs text-gray-400 uppercase tracking-wider mb-3">
        Mennyire érdekel?
      </div>
      
      {intentButtons.map((intent) => (
        <button
          key={intent.key}
          onClick={() => handleIntentClick(intent.key)}
          disabled={loading}
          className={`
            w-full flex items-center gap-3 px-4 py-3 rounded-xl
            transition-all duration-200
            ${getButtonStyle(intent.key)}
            ${selectedIntent === intent.key ? 'text-white' : 'text-gray-300'}
            disabled:opacity-50
            transform hover:scale-[1.02]
          `}
        >
          <span className="text-xl">{intent.icon}</span>
          <span className="font-semibold">{intent.label}</span>
          {selectedIntent === intent.key && (
            <span className="ml-auto text-xs bg-white/20 px-2 py-0.5 rounded-full">
              Kiválasztva
            </span>
          )}
        </button>
      ))}

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 max-w-sm w-full">
            <div className="text-3xl text-center mb-4">
              {INTENT_LEVELS[selectedIntent]?.icon}
            </div>
            <h3 className="text-xl font-bold text-white text-center mb-2">
              {INTENT_LEVELS[selectedIntent]?.label}?
            </h3>
            <p className="text-gray-400 text-center mb-6">
              {selectedIntent === 'MAKE_OFFER' 
                ? 'Az eladó értesítést kap, hogy ajánlatot szeretnél tenni. Biztos vagy benne?'
                : 'Kapcsolatba lépünk veled és az eladóval. Folytatod?'}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmation(false)}
                className="flex-1 px-4 py-3 rounded-xl bg-gray-700 text-white font-semibold hover:bg-gray-600 transition-colors"
              >
                Mégsem
              </button>
              <button
                onClick={() => submitIntent(selectedIntent)}
                disabled={loading}
                className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold hover:from-green-500 hover:to-emerald-500 transition-colors disabled:opacity-50"
              >
                {loading ? 'Feldolgozás...' : 'Igen, küldés'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntentButtons;
